import React, { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Icon from 'Components/Icon';
import { classnames } from 'Utils/classnames';

import EnvironmentManager from 'Utils/environmentManager';
import { TJK_URL_SUFFIX, isTjkPlayDisable } from 'Store/constants/horseRacing';
import TextWrapper from 'Components/TextWrapper';
import { doubleDigitDecimalFormatter } from 'Utils/decimalFormatter';
import { betSlipOpened, betSlipClosed, updateUserBalance } from 'Store/actions';
import LocalStorage from 'Utils/localStorage';
import { withMixpanel } from 'Utils/mixpanel';
import NoSSR from 'Components/NoSSR';
import { getBulletinTargetPath } from 'Utils/getBulletinTargetPath';
import { shouldRefreshBalanceOnBetSlipOpen } from 'Utils/accountBalancePolling';

const StickyNavigation = ({ type = null, isDesktop, mixpanel }) => {
  const isHorseRacing = type === 'horse-racing';
  const isChanceGames = type === 'chance-games';
  const dispatch = useDispatch();
  const location = useLocation();

  const navItemHomeTitle = isChanceGames ? EnvironmentManager.getMessageText({ key: 'title_navbar_digital_games_santra' }) : EnvironmentManager.getMessageText({ key: 'tab_name_santra' }) || 'Keşfet';
  const navItemIddaaTitle = EnvironmentManager.getMessageText({ key: 'tab_name_do_bet' }) || 'Bahis Yap';
  const navItemTribunTitle = EnvironmentManager.getMessageText({ key: 'tab_name_tribune' }) || 'Tribün';
  const navItemCouponsTitle = EnvironmentManager.getMessageText({ key: 'tab_name_my_coupons' }) || 'Kuponlarım';
  const navItemChanceGamesTitle = EnvironmentManager.getMessageText({ key: 'title_navbar_digital_games_dg' }) || 'Şans Oyunları';
  const navItemHelpTitle = EnvironmentManager.getMessageText({ key: 'title_navbar_digital_games_helpmenu' }) || 'Yardım';

  const navItemProgramTitle = EnvironmentManager.getMessageText({ key: 'tab_tjk_program' }) || 'Program';
  const navItemResultsTitle = EnvironmentManager.getMessageText({ key: 'tab_tjk_results' }) || 'Sonuçlar';

  const isTjkPlayDisableFlag = isTjkPlayDisable();

  const activeClassName = 'sticky-navigation__item--active';
  const navItemIddaaLink = '/iddaa';
  const betSlip = useSelector(state => state.betSlip);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const allCategories = useSelector(state => state.allCategories);
  const subHeader = useSelector(state => state.home.subHeader);
  const { bulletinTabs } = useSelector(state => state.sportsBook.sportsBookTabs);
  const isHomePage = location?.pathname === '/';
  const isHasTopWin = betSlip?.couponDetails.some(coupon => coupon.hasTopWin);
  const zirveOranSwitch = EnvironmentManager.getMessageText({ key: 'zirveoran_switch' }) == 'true';
  const { topWinEnabled } = useSelector(state => state?.auth?.data);
  const calculateTotalOdds = (couponDetails, useTopWin = true) => {
    let totalOdds = 1;
    couponDetails.forEach(coupon => {
      const bet = coupon.bets[0];
      const topWinOdd = useTopWin && bet.topWinOdds ? parseFloat(bet.topWinOdds) : null;
      const odd = parseFloat(bet.odd);
      totalOdds *= topWinOdd || odd;
    });
    return totalOdds;
  };

  const totalOddsWithTopWin = calculateTotalOdds(betSlip?.couponDetails, true);
  const totalOddsWithoutTopWin = calculateTotalOdds(betSlip?.couponDetails, false);

  const iddaaTargetPath = getBulletinTargetPath({
    path: navItemIddaaLink,
    bulletinTabs: bulletinTabs
  });

  const onClickIddaaLink = () => {
    mixpanel.track('Click Main Page Event List on Tab Bar');
  };

  const onClickTribunLink = () => {
    mixpanel.track('Click Main Page Tribun', { 'Area Type': 'Navigation Bar' });
  };

  const betSlipToggle = (event, type) => {
    event.stopPropagation();
    LocalStorage.set('betSlipTogglerWithClick', type);
    if (!betSlip.isOpen) {
      dispatch(betSlipOpened());
      LocalStorage.set('betSlipToggler', true);
    } else {
      dispatch(betSlipClosed());
      LocalStorage.set('betSlipToggler', false);
    }
    if (betSlip.isOpen && shouldRefreshBalanceOnBetSlipOpen({
      isAuthenticated,
      isBalanceSSEEnabled: EnvironmentManager.getParameter('user_sse_enabled_web') === true
    })) {
      dispatch(updateUserBalance());
    }
  };

  const homeIcon = <NavLink
    exact
    to='/'
    className='sticky-navigation__item'
    key='home'
    title={ navItemHomeTitle }
    activeClassName={ activeClassName }
  >

    <i className="bly bly-home bly-size24" />
    <span className="sticky-navigation__item-text">{ navItemHomeTitle }</span>
  </NavLink>;

  const betSlipIcon = <div
    className='sticky-navigation__item'
  >
    <div className='sticky-navigation__betslip-wrapper' onClick={ event => betSlipToggle(event, 'click') }>
      <div className={ classnames({
        'sticky-navigation__betslip': true,
        'sticky-navigation__betslip--valid': betSlip.isMBSValid && betSlip.isMBSCombinationValid
      }) }>
        <div className={ classnames({
          'sticky-navigation__betslip--top': true,
          'sticky-navigation__betslip--top--valid': betSlip.isMBSValid && betSlip.isMBSCombinationValid
        }) }><span>{ betSlip.atrium.betCount }</span> Maç</div>
        <div className={ classnames({
          'sticky-navigation__betslip--bottom': true,
          'sticky-navigation__betslip--bottom--valid': betSlip.isMBSValid && betSlip.isMBSCombinationValid
        }) }>
          { /* eslint-disable-next-line no-nested-ternary */ }
          <TextWrapper text={ betSlip.atrium.odd === 0 ? '0,00' : ((isHasTopWin && zirveOranSwitch && topWinEnabled) ? doubleDigitDecimalFormatter(totalOddsWithTopWin) : (isHasTopWin && zirveOranSwitch && !topWinEnabled) ? doubleDigitDecimalFormatter(totalOddsWithoutTopWin) : doubleDigitDecimalFormatter(betSlip.atrium.odd)) } limit={ '6' } dotted />
        </div>
      </div>
    </div>
  </div>;

  const horseRacingButton = <NavLink
    key='horceRacing'
    to={ TJK_URL_SUFFIX.program.fullPage }
    className='sticky-navigation__item sticky-navigation__item__flex-end pointer-events-all'
    title={ navItemProgramTitle }
    activeClassName={ activeClassName }
    isActive={ (match, location) => {
      return location.pathname.includes(TJK_URL_SUFFIX.program.fullPage) || location.pathname.includes(TJK_URL_SUFFIX.fixedOdd.fullPage);
    } }
  >
    <Icon iconName="svgHorseRacing" className="sticky-navigation__item--icon-horse" />
    <span className="sticky-navigation__item-text"> { navItemProgramTitle }</span>
  </NavLink>;

  const iddaaButton = <NavLink

    key='iddaa'
    to={ iddaaTargetPath }
    className='sticky-navigation__item'
    title={ navItemIddaaTitle }
    activeClassName={ activeClassName }
    isActive={ (match, location) => {
      if (location.pathname.includes('populer-bahisler')
      || location.pathname.includes('trend-olan-maclar')
      || location.pathname.includes('sana-ozel-maclar')) {
        return false;
      }
      const url = location.pathname.split('/')[1];
      return ['iddaa', 'canli-iddaa'].includes(url);
    } }
  >
    <div onClick={ () => onClickIddaaLink() }>
      <i className="bly bly-iddaa bly-size24" />
      <span className="sticky-navigation__item-text"> { navItemIddaaTitle }</span>
    </div>
  </NavLink>;

  const horseRacingHomeIcon =  <NavLink
    key='horcaRaceAuthor'
    to={  TJK_URL_SUFFIX.home.fullPage }
    title="At Yarışı"
    className='sticky-navigation__item sticky-navigation__item__flex-end'
    activeClassName={ activeClassName }
    isActive={ (match, location) => {
      return location.pathname === TJK_URL_SUFFIX.home.fullPage;
    } }
  >
    <Icon iconName="svgShoe" className="sticky-navigation__item--horse-racing-home"/>
    <span className="sticky-navigation__item-text">At Yarışı</span>
  </NavLink>;

  const tribunIcon = <NavLink
    key='tribun'
    to='/tribun'
    title={ navItemTribunTitle }
    className='sticky-navigation__item'
    activeClassName={ activeClassName }
  >
    <div onClick={ () => onClickTribunLink() }>
      <i className="bly bly-tribune bly-size24" />
      <span className="sticky-navigation__item-text">{ navItemTribunTitle }</span>
    </div>
  </NavLink>;

  const resultsButton = <NavLink
    key='horceRacing'
    to={ TJK_URL_SUFFIX.result.fullPage }
    className='sticky-navigation__item sticky-navigation__item__flex-end'
    title={ navItemProgramTitle }
    activeClassName={ activeClassName }
  >
    <Icon iconName={ location?.pathname?.includes('tjk') && location?.pathname?.includes(TJK_URL_SUFFIX.result.page) ? 'svgFinishFlagGreen' : 'svgFinishFlag' } className="sticky-navigation__item--icon-results" />
    <span className="sticky-navigation__item-text"> { navItemResultsTitle }</span>
  </NavLink>;

  const couponsIcon =  <NavLink
    key='kuponlarım'
    to={ isHorseRacing ? TJK_URL_SUFFIX.coupons.fullPage : '/kuponlarim' }
    title={ navItemCouponsTitle }
    className='sticky-navigation__item'
    activeClassName={ activeClassName }
  >
    <i className="bly bly-coupons bly-size24" />
    <span className="sticky-navigation__item-text">{ navItemCouponsTitle }</span>
  </NavLink>;

  const chanceGamesIcon =  <NavLink
    key='changeGames'
    to={ '/sans-oyunlari' }
    className='sticky-navigation__item'
    title={ navItemChanceGamesTitle }
    activeClassName={ activeClassName }
    isActive={ (_, location) => {
      return location.pathname === '/sans-oyunlari';
    } }
  >
    <i className="bly bly-chance-games bly-size24" />

    <span className="sticky-navigation__item-text">{ navItemChanceGamesTitle }</span>
  </NavLink>;

  const chanceGamesHelpIcon = <NavLink
    key='changeGamesHelp'
    to={ '/sans-oyunlari/yardim' }
    title={ navItemHelpTitle }
    className='sticky-navigation__item'
    activeClassName={ activeClassName }
  >

    <Icon iconName="svgHelp" className="sticky-navigation__item--icon-chance-games-help" />
    <span className="sticky-navigation__item-text">{ navItemHelpTitle }</span>
  </NavLink>;

  let navigationIcons = [homeIcon, iddaaButton, !isDesktop ? betSlipIcon : null, tribunIcon, couponsIcon];
  if (isHorseRacing) {
    if (isTjkPlayDisableFlag) {
      navigationIcons = [homeIcon, horseRacingHomeIcon, horseRacingButton, resultsButton];
    } else {
      navigationIcons = [homeIcon, horseRacingHomeIcon, horseRacingButton, resultsButton, couponsIcon];
    }
  } else if (isChanceGames) {
    navigationIcons = [homeIcon, chanceGamesIcon, chanceGamesHelpIcon];
  }

  const classes = classnames({
    'sticky-navigation__content': true,
    'sticky-navigation__content--forced-mobile': !isDesktop,
    'sticky-navigation__content__chance-games': isChanceGames,
    'sticky-navigation__content__tjk-play-disable': isHorseRacing && isTjkPlayDisableFlag
  });

  const scrollToTop = () => {
    if (location?.pathname?.includes('iddaa')) {
      const sportsbookList = document.getElementsByClassName('sportsbookList')[0];
      if (sportsbookList)
        sportsbookList.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else if (location?.pathname?.includes('tribun')) {
      if (window !== undefined)
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const hasAllCategoriesData = allCategories?.data?.all && Array.isArray(allCategories.data.all) && allCategories.data.all.length > 0;
  const hasSubHeaderData = subHeader?.data && Array.isArray(subHeader.data) && subHeader.data.length > 0;

  const isStickyNavLoading = !isDesktop && isHomePage && (
    allCategories?.isLoading
    || subHeader?.isLoading
    || !hasAllCategoriesData
    || !hasSubHeaderData
  );

  const renderStickyNavSkeleton = () => (
    <div className="sticky-navigation-skeleton">
      <div className="sticky-navigation-skeleton__content">
        { Array.from({ length: 5 }).map((_, index) => (
          <div key={ index } className="sticky-navigation-skeleton__item">
            <div className="sticky-navigation-skeleton__item__icon" />
            <div className="sticky-navigation-skeleton__item__text" />
          </div>
        )) }
      </div>
      <div className="sticky-navigation-skeleton__iosFixer"/>
    </div>
  );

  return (
    <>
      { isStickyNavLoading
        ? renderStickyNavSkeleton()
        : <div className="sticky-navigation">
          <NoSSR>
            <div onClick={ () => scrollToTop() } className={ classes }>
              { navigationIcons.map((e, index) => (<Fragment key={ `navigation-icons-${index}` }>{
                e }</Fragment>)) }
            </div>
          </NoSSR>
          <div className="sticky-navigation__iosFixer"/>
        </div>
      }
    </>
  );
};

StickyNavigation.propTypes = {
  path: PropTypes.string,
  type: PropTypes.string,
  isDesktop: PropTypes.bool,
  mixpanel: PropTypes.object,
};

export default withMixpanel(StickyNavigation);
